import { FC } from 'react';
import { Check, CloseThin } from '~/components/Icons/fill';
import VRCOfferStyled from './VRCOfferStyled';
import Button from '~/components/Button';
import { Modals, Product } from '~/types/products';
import { useSelector } from 'react-redux';
import { ReduxState } from '~/types/redux';
import Tooltip from '~/components/Tooltip';
import { InfoCircle } from '~/components/Icons/outline';
import { PLAN_SPEEDS_IDS } from '~/constants/products';
import { trackEvent } from '~/helpers/tracking';
import router from 'next/router';

interface ScreenProps {
  visible: boolean;
  handleSelectPlan?: (product: Product, scroll: boolean) => void;
  setActiveModal: (key?: Modals) => void;
  buttons?: {
    select: {
      text: string;
      backgroundColor: string;
      borderColor: string;
      textColor: string;
      hoverBackgroundColor: string;
      hoverBorderColor: string;
      hoverTextColor: string;
      activeText?: string;
    };
  };
  metadata?: {
    vrcExperience?: {
      vrcExperienceModalHeadline?: string;
      vrcExperienceModalSubheadline?: string;
      vrcExperienceModalTooltip?: string;
      vrcExperienceModalCTA?: string;
      vrcExperienceModalPlanSpeed?: string;
    };
  };
}

interface CheckListItemProps {
  children: string | JSX.Element | (string | JSX.Element)[];
}

const CheckListItem: FC<CheckListItemProps> = ({ children }) => {
  return (
    <li className="vrc-offer-screen__list-item">
      <Check className="vrc-offer-screen__list-item-icon" />
      <span className="vrc-offer-screen__list-item-text">{children}</span>
    </li>
  );
};

CheckListItem.displayName = 'CheckListItem';

const VRCOffer: FC<ScreenProps> = ({ visible, handleSelectPlan, setActiveModal, buttons, metadata }) => {
  const { products } = useSelector((store: ReduxState) => store);
  const currentPage = router.pathname;
  const tooltipText = metadata?.vrcExperience?.vrcExperienceModalTooltip || (
    <>
      You will receive an email including the $100 Visa<sup>&reg;</sup> reward card details within 24 hours of your
      online submitted order. One-year agreement, other terms and redemption requirements apply.
    </>
  );

  // Grab plan speed from editable var or default to 1gig plan
  const planSpeedCta = products?.available?.broadband?.find((product) => {
    const targetSpeed = metadata?.vrcExperience?.vrcExperienceModalPlanSpeed || '1000';
    return product?.productId === PLAN_SPEEDS_IDS[targetSpeed];
  });

  if (!visible) return null;

  const handleButtonClick = () => {
    planSpeedCta.isVrcSelected = true;
    handleSelectPlan(planSpeedCta, true);
    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'Modal',
        location: currentPage,
        position: 'VRC Offer Modal',
        text: "Don't miss this exclusive online only offer!",
      },
    });
  };

  trackEvent({
    action: 'elementViewed',
    data: {
      elementType: 'Modal',
      location: currentPage,
      position: 'VRC Offer Modal',
      text: "Don't miss this exclusive online only offer!",
    },
  });

  const handleClose = () => {
    setActiveModal();

    trackEvent({
      action: 'elementClicked',
      data: {
        elementType: 'Modal Close',
        text: 'X',
        location: currentPage,
        position: 'VRC Offer Modal',
      },
    });
  };

  return (
    <VRCOfferStyled>
      <CloseThin onClick={handleClose} className="vrc-offer-screen__close-button" />
      <h3 className="vrc-offer-screen__heading">
        {metadata?.vrcExperience?.vrcExperienceModalHeadline || "Don't miss this exclusive online only offer!"}
      </h3>
      <p className="vrc-offer-screen__subheading">
        {metadata?.vrcExperience?.vrcExperienceModalSubheadline || (
          <>
            Order Fiber 1 Gig today, get a $100 Visa<sup>&reg;</sup> rewards card. Online only.
          </>
        )}
        <Tooltip
          position="top"
          theme="light"
          html={
            <div style={{ width: 200 }}>
              <div className="">{tooltipText}</div>
            </div>
          }
        >
          <InfoCircle />
        </Tooltip>
      </p>
      <Button className="vrc-offer-screen__btn" onClick={handleButtonClick} buttonConfig={buttons?.select}>
        {metadata?.vrcExperience?.vrcExperienceModalCTA || 'Get 1 Gig'}
      </Button>
    </VRCOfferStyled>
  );
};

VRCOffer.displayName = 'VRCOffer';

export default VRCOffer;
